import { styles } from "@repo/helpers";
import { Loading, MapChart, Marker } from "@repo/ui";
import { useCallback, useMemo, useState } from "react";

import { PinStyle } from "./markers";
import style from "./style.module.scss";

export interface PinFilter<TPinType extends number> {
	activePinTypes: Set<TPinType>;
	isActive: (pinType: TPinType) => boolean;
	toggle: (pinType: TPinType) => void;
}

export function usePinFilter<TPinType extends number>(initialPinTypes: TPinType[]): PinFilter<TPinType> {
	const [activePinTypes, setActivePinTypes] = useState(() => new Set(initialPinTypes));

	const isActive = useCallback((pinType: TPinType) => activePinTypes.has(pinType), [activePinTypes]);

	const toggle = useCallback((pinType: TPinType) => {
		setActivePinTypes((prev) => {
			const next = new Set(prev);
			if (next.has(pinType)) next.delete(pinType);
			else next.add(pinType);
			return next;
		});
	}, []);

	return useMemo(() => ({ activePinTypes, isActive, toggle }), [activePinTypes, isActive, toggle]);
}

interface LegendItem<TPinType extends number> {
	pinType: TPinType;
	text: string;
}

interface PinMapProps<TPinType extends number> {
	id: string;
	loading: boolean;
	markers: Marker[];
	legend: LegendItem<TPinType>[];
	pinStyles: Record<TPinType, PinStyle>;
	filter: PinFilter<TPinType>;
}

export function PinMap<TPinType extends number>({
	id,
	loading,
	markers,
	legend,
	pinStyles,
	filter,
}: PinMapProps<TPinType>) {
	return (
		<Loading loading={loading}>
			<div className={style.container}>
				<MapChart data={markers} type="map" mapType="roadmap" id={id} height="60vh" />
				<div className={style.legend}>
					{legend
						.filter((item) => item.text)
						.map((item) => {
							const isActive = filter.isActive(item.pinType);
							return (
								<div
									key={item.pinType}
									className={style.legendItem}
									onClick={() => filter.toggle(item.pinType)}
								>
									<div
										className={style.legendCircle}
										style={{ backgroundColor: isActive ? pinStyles[item.pinType].color : "gray" }}
									/>
									<span className={styles(style.legendText, !isActive && style.inactive)}>
										{item.text}
									</span>
								</div>
							);
						})}
				</div>
			</div>
		</Loading>
	);
}
